import SideBarTool from '@/commons/components/side-bar-tool';
import './index.less';
import { useEffect, useState, useRef, useContext } from 'react';
import { SideBarBtnType } from '@/commons/enums/side-bar-btn-type';
import { Skeleton } from 'antd';
import { useParams } from 'react-router-dom';
import { SystemContext } from '@/App';
import { SystemStatus } from '@/commons/context/system-store';
import { Group } from '@/commons/interface/speccommon/group';
import classNames from 'classnames';
import TnInput from '@/commons/widget/input/tn-input';
import { addGroup, getGroups } from '@/api/spec-common/group';
import genUUID from '@/commons/utils/gen-uuid';
import TnMessage from '@/commons/widget/message';
import GroupItem from './group-item';
import { graphicStore } from '@/commons/store/graphic-store';


interface IProps {

}

export default function GroupPanel(props: IProps) {

    const { projectUuid, modelUuid, branchUuid } = useParams();

    const systemContext = useContext<SystemStatus>(SystemContext);

    const [activeItem, setActiveItem] = useState<Group>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [adding, setAdding] = useState<boolean>(false);
    const [addName, setAddName] = useState<string>('');
    const [list, setList] = useState<Group[]>([]);
    const [treeList, setTreeList] = useState<Group[]>([]);

    useEffect(() => {
        if (!projectUuid || !modelUuid) return;
        generateData();
    }, [projectUuid, modelUuid])

    useEffect(() => {
        if (!projectUuid || !modelUuid) return;
        if (!graphicStore?.extraContext?.getCurrentViewEditor()) return;
        graphicStore?.extraContext?.getCurrentViewEditor().listeners.signals.onGroupChanged.add(generateData);

        return () => {
            graphicStore?.extraContext?.getCurrentViewEditor().listeners.signals.onGroupChanged.remove(generateData);
        }
    }, [graphicStore?.extraContext?.getCurrentViewEditor()])

    useEffect(() => {
        if (!list || list?.length === 0) {
            setTreeList([]);
            return;
        }
        let treeList: Group[] = buildTree(list);
        setTreeList(treeList);
    }, [list])

    const generateData = () => {
        setLoading(true);
        getGroups(projectUuid, modelUuid).then(res => {
            if (res.status === 200) {
                let data: Group[] = res.data || [];
                data?.forEach(item => {
                    item.hidden = false; // 默认全部显示
                    item.expand = false; // 默认全部折叠
                })
                setList(data);
            }
        }).catch(err => {
            TnMessage.error("获取组失败");
        }).finally(() => {
            setLoading(false);
        })
    }

    const buildTree = (groups: Group[]) => {
        if (groups?.length === 0) return [];
        let groupMap: { [key: string]: Group } = {};
        let tree: (Group & { level?: number, expand?: boolean })[] = [];
        // let tree: (Group & { level?: number })[] = [];
        // 构建dict
        groups?.forEach(group => {
            groupMap[group?.uuid] = { ...group, children: [], level: 1 };
        });
        // 构建树
        groups.forEach(group => {
            const parent = groupMap[group?.parentUuid];
            if (parent) {
                parent?.children.push(groupMap[group?.uuid]);
                groupMap[group?.uuid].level = (parent?.level || 1) + 1;
            } else {
                tree.push(groupMap[group?.uuid]);
            }
        });

        return tree;
    };

    const renderTree = (nodes: Group[]) => {
        return (
            <>
                {nodes?.map(item => (
                    <>
                        <GroupItem
                            key={item?.uuid}
                            group={item}
                            active={activeItem?.uuid === item?.uuid}
                            onClick={setActiveItem}
                            onDelete={(uuid: string) => {
                                let newList = deleteTreeNode(list, uuid);
                                setList(newList);
                            }}
                            onUpdate={(group: Group) => {
                                let idx = list?.findIndex(i => i?.uuid === item?.uuid);
                                list.splice(idx, 1, group);
                                setList([...list]);
                            }}
                            onCreate={(group: Group) => {
                                if (group?.parentUuid) {
                                    let idx = list?.findIndex(i => i?.uuid === group?.parentUuid);
                                    list[idx].expand = true; // 创建子项就展开
                                }
                                setList([...list, group]);
                            }}
                            setLoading={setLoading}
                            getGenerationUuids={getGenerationUuids}
                            showAll={showAll}
                            showOne={showOne}
                            toggleExpand={() => {
                                let idx = list?.findIndex(i => i?.uuid === item?.uuid);
                                list[idx].expand = !list[idx].expand;
                                setList([...list]);
                            }}
                        />
                        {item.children && item.children.length > 0 && item.expand && (
                            <div style={{ paddingLeft: item?.level * 8 }}>
                                {renderTree(item.children)}
                            </div>
                        )}
                    </>
                ))
                }
            </>
        );
    };

    const onAddBtnClick = () => {
        setAddName("");
        setAdding(true);
    }

    const onAddSave = () => {
        if (!addName || addName?.trim()?.length === 0) {
            setAddName("");
            setAdding(false);
            return;
        }
        let uuid = genUUID();
        let group: Group = {
            uuid: uuid,
            name: addName,
            projectUuid: projectUuid,
            modelUuid: modelUuid,
        }
        setLoading(true);
        addGroup(projectUuid, modelUuid, group, uuid, false).then(res => {
            if (res.status === 200) {
                let newGroup = res.data;
                // group = {
                //     ...group,
                //     id: newGroup.id,
                //     buildingUuid: newGroup.buildingUuid,
                // }
                setList([...list, newGroup]);
                TnMessage.success("添加成功");
            }
        }).catch(err => {
            TnMessage.error("添加失败");
        }).finally(() => {
            setLoading(false);
            setAddName("");
            setAdding(false);
        })
    }

    // 获取子孙节点(包括自己)
    const getDescendantUuids = (uuid: string, groups: Group[]): string[] => {
        let descendants: string[] = [uuid];
        groups.forEach(group => {
            if (group?.parentUuid === uuid) {
                descendants = descendants.concat(getDescendantUuids(group?.uuid, groups));
            }
        });
        return descendants;
    };

    // 递归删除子孙节点
    const deleteTreeNode = (list: Group[], uuid: string) => {
        const uuidsToDelete = getDescendantUuids(uuid, list);
        return list?.filter(group => !uuidsToDelete.includes(group?.uuid));
    };

    const getGenerationUuids = (uuid: string) => {
        let res: string[] = [];
        let currentUuid = uuid;
        let groupMap: { [key: string]: Group } = {};
        // 构建dict
        list?.forEach(group => {
            groupMap[group.uuid] = group;
        });
        // 递归至祖先
        while (!!currentUuid) {
            res.push(currentUuid);
            const parentUuid = groupMap[currentUuid]?.parentUuid;
            if (parentUuid) {
                currentUuid = parentUuid;
            } else {
                break;
            }
        }
        return res;
    }

    const showAll = () => {
        list?.forEach(item => {
            item.hidden = false;
        })
        setList([...list]);
    }

    const showOne = (uuid: string) => {
        let active = null;
        list?.forEach(item => {
            if (uuid === item?.uuid) {
                item.hidden = false;
                active = item;
            } else {
                item.hidden = true;
            }
        })
        setList([...list]);
        setActiveItem({ ...active });
    }

    return (
        <div className='side-bar-main'>
            <SideBarTool
                activeBtnList={[SideBarBtnType.ADD]}
                onAddBtnClick={onAddBtnClick}
            />
            <div className="body">
                <div className="title">
                    组
                </div>
                <Skeleton loading={loading} active>
                    <div className='group-panel'>
                        {adding && <div className={classNames({
                        })} id="add-input">
                            <TnInput
                                value={addName}
                                size='small'
                                onTextChange={(value) => setAddName(value)}
                                onPressEnter={onAddSave}
                                onEscapeKeyDown={() => {
                                    setAdding(false);
                                    setAddName("");
                                }}
                                onBlur={onAddSave}
                                placeholder='输入组名称'
                                autoFocus
                            />
                        </div>}
                        {renderTree(treeList)}
                    </div>
                </Skeleton>
            </div>
        </div>
    )
}